import { useTheme } from '@shopify/restyle';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { ActivitySectorChipsField } from 'app/components/profile/activity-sector-chips-field';
import { useScopedTranslation } from 'app/hooks/i18n';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { useRef } from 'react';
import type { TextInput } from 'react-native';
import type { Profile } from 'app/network/profile';
import { Trans } from 'react-i18next';
import type { ProfileInformationFormValues } from 'app/screens/profile/my-information/schema';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import type { Theme } from 'app/shopify';
import type { Control } from 'react-hook-form';

const activitySectorFeatureFlag = FeatureFlag.useActivitySector;

interface SectionProps {
  control: Control<ProfileInformationFormValues>;
  profile?: Profile;
}

export function PersonalInformationSection({ control, profile }: SectionProps) {
  const theme = useTheme<Theme>();
  const { t } = useScopedTranslation();
  const refAdditionalName = useRef<TextInput>(null);
  const refLastName = useRef<TextInput>(null);
  const refBio = useRef<TextInput>(null);
  const userInfo = useAtomValue(userInfoAtom);

  return (
    <Column
      gap="spacing.general.x4"
      style={{
        backgroundColor: theme.colors['surface.default'],
        marginHorizontal: theme.spacing['spacing.general.x4'],
        padding: theme.spacing['spacing.general.x4'],
        borderRadius: theme.borderRadii['radius.md'],
        borderWidth: 1,
        borderColor: theme.colors['surface.disabled'],
      }}
    >
      <Column gap="spacing.general.x1">
        <Text variant="bodyLarge" fontWeight="700" color="text.primary">
          <Trans i18nKey="personalInformationTitle" ns="my-information" defaults="Personal information" />
        </Text>
        <Text variant="bodyBase" color="text.placeholder">
          <Trans
            components={{ strong: <Text fontWeight="700" color="text.placeholder" /> }}
            i18nKey="personalInformationText"
            ns="my-information"
          />
        </Text>
      </Column>

      <ControlledInput
        {...{ control }}
        autoCapitalize="words"
        autoComplete="name-given"
        editable={Boolean(profile)}
        label={t('firstNameFieldLabel', {
          ns: 'my-personal-information',
        })}
        markAsRequired
        name="firstName"
        onSubmitEditing={() =>
          isFeatureEnabled(FeatureFlag.additionalName, userInfo?.featureFlags)
            ? refAdditionalName.current?.focus()
            : isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags)
              ? refLastName.current?.focus()
              : refBio.current?.focus()
        }
        placeholder={t('firstNameFieldPlaceholder', {
          ns: 'my-personal-information',
        })}
        returnKeyType="next"
        submitBehavior="submit"
      />

      {isFeatureEnabled(FeatureFlag.additionalName, userInfo?.featureFlags) && (
        <ControlledInput
          {...{ control }}
          autoCapitalize="words"
          editable={Boolean(profile)}
          innerRef={refAdditionalName}
          label={t('additionalNameFieldLabel', {
            ns: 'my-personal-information',
          })}
          name="additionalName"
          onSubmitEditing={() =>
            isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags)
              ? refLastName.current?.focus()
              : refBio.current?.focus()
          }
          placeholder={t('additionalNameFieldPlaceholder', {
            ns: 'my-personal-information',
          })}
          returnKeyType="next"
          submitBehavior="submit"
        />
      )}

      {isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags) && (
        <ControlledInput
          {...{ control }}
          autoCapitalize="words"
          autoComplete="name-family"
          editable={Boolean(profile)}
          innerRef={refLastName}
          label={t('lastNameFieldLabel', {
            ns: 'my-personal-information',
          })}
          markAsRequired
          name="lastName"
          onSubmitEditing={() => refBio.current?.focus()}
          placeholder={t('lastNameFieldPlaceholder', {
            ns: 'my-personal-information',
          })}
          returnKeyType="next"
          submitBehavior="submit"
        />
      )}

      {isFeatureEnabled(activitySectorFeatureFlag, userInfo?.featureFlags) ? <ActivitySectorChipsField /> : null}

      <ControlledInput
        {...{ control }}
        autoCapitalize="sentences"
        editable={Boolean(profile)}
        innerRef={refBio}
        label={t('bioFieldLabel', {
          ns: 'my-personal-information',
        })}
        labelSuffix={`(${t('suggestede', { ns: 'common' })})`}
        maxLength={500}
        multiline
        name="bio"
        placeholder={t('bioFieldPlaceholder', {
          ns: 'my-personal-information',
        })}
        returnKeyType="default"
        scrollEnabled={false} // Required for multiline input to scroll into view, when given focus and the keyboard is shown
      />
    </Column>
  );
}
